import useMediaQuery from '@mui/material/useMediaQuery'
import { createContext, ReactNode, useContext, useMemo } from 'react'
import { isGuest } from 'utils/user'

export enum AppMode {
  condensedWidthAndHeight = 'condensedWidthAndHeight',
  condensedHeight = 'condensedHeight',
  condensedWidth = 'condensedWidth',
  normal = 'normal'
}

interface AppModeProviderProps {
  children: ReactNode
}

interface AppModeContextValue {
  mode: AppMode
  isCondensedHeight: boolean
  isCondensedWidth: boolean
}

const AppModeContext = createContext<AppModeContextValue>({
  mode: AppMode.normal,
  isCondensedHeight: false,
  isCondensedWidth: false
})

export default function AppModeProvider(props: AppModeProviderProps) {
  const isCondensedHeight = useMediaQuery('(max-height: 500px)', {
    noSsr: true
  })
  const isCondensedWidth = useMediaQuery(
    isGuest() ? '(max-width: 950px)' : '(max-width: 1300px)',
    {
      noSsr: true
    }
  )

  const mode = useMemo(() => {
    if (isCondensedHeight && isCondensedWidth)
      return AppMode.condensedWidthAndHeight
    if (isCondensedHeight) return AppMode.condensedHeight
    if (isCondensedWidth) return AppMode.condensedWidth
    return AppMode.normal
  }, [isCondensedHeight, isCondensedWidth])

  const value: AppModeContextValue = useMemo(
    () => ({ mode, isCondensedHeight, isCondensedWidth }),
    [mode, isCondensedHeight, isCondensedWidth]
  )

  return (
    <AppModeContext.Provider value={value}>
      {props.children}
    </AppModeContext.Provider>
  )
}

export function useAppMode(): AppModeContextValue {
  return useContext(AppModeContext)
}
